import { combineCompetitionBuckets } from './competition';
import { combineActivityBuckets } from './activity';

const CONTROLLED_SEGMENTS = ['municipal', 'public_private', 'private', 'nursery_public', 'nursery_private', 'maluch_public', 'maluch_private'];
const ALL_DATA_SEGMENTS = [...CONTROLLED_SEGMENTS, 'unverified'];

function activeDataSegments(selected = []) {
  const normalized = CONTROLLED_SEGMENTS.filter((segment) => selected.includes(segment));
  return normalized.length === CONTROLLED_SEGMENTS.length ? ALL_DATA_SEGMENTS : normalized;
}

function kidplaceAreaScore(children, facilities, nurseries, childrenKnownTargets) {
  const f = Number(facilities || 0);
  if (f <= 0) return null;
  const n = Number(nurseries || 0);
  const c = Number(children || 0);
  const known = Number(childrenKnownTargets || 0);

  const facilityPoints = f === 1 ? 7 : f <= 3 ? 15 : f <= 7 ? 23 : f <= 15 ? 30 : 35;
  const nurseryPoints = n <= 0 ? 0 : n === 1 ? 4 : n <= 3 ? 8 : n <= 7 ? 12 : 15;
  let raw = facilityPoints + nurseryPoints;
  let knownMax = 50;

  if (known === f) {
    const childrenPoints = c <= 49 ? 8 : c <= 99 ? 15 : c <= 199 ? 25 : c <= 399 ? 35 : c <= 799 ? 43 : 50;
    raw += childrenPoints;
    knownMax += 50;
  }
  return Math.round((100 * raw) / knownMax);
}

function effectiveScore(baseScore, deltaSum, deltaCount) {
  if (baseScore === null || baseScore === undefined) return null;
  const count = Number(deltaCount || 0);
  if (count <= 0) return baseScore;
  return Math.max(0, Math.min(100, Math.round(Number(baseScore) + Number(deltaSum || 0) / count)));
}

function combineBuckets(buckets = {}, selected = []) {
  const keys = activeDataSegments(selected);
  const total = {
    potential_arr: 0,
    children_total: 0,
    children_known_targets: 0,
    targets_count: 0,
    own_units_count: 0,
    valued_targets_count: 0,
    nurseries_count: 0,
    effective_delta_sum: 0,
    effective_delta_count: 0,
  };

  keys.forEach((key) => {
    const bucket = buckets?.[key];
    if (!bucket) return;
    Object.keys(total).forEach((field) => {
      total[field] += Number(bucket[field] || 0);
    });
  });

  const score = kidplaceAreaScore(
    total.children_total,
    total.targets_count,
    total.nurseries_count,
    total.children_known_targets,
  );
  return {
    ...total,
    potential_mrr: total.potential_arr / 12,
    units_count: total.targets_count + total.own_units_count,
    children_complete: total.targets_count > 0 && total.targets_count === total.children_known_targets,
    arr_complete: total.targets_count > 0 && total.targets_count === total.valued_targets_count,
    score,
    effective_score: effectiveScore(score, total.effective_delta_sum, total.effective_delta_count),
  };
}

function areaTotals(items = []) {
  const totals = {
    potential_arr: 0,
    potential_mrr: 0,
    children_total: 0,
    children_known_targets: 0,
    targets_count: 0,
    own_units_count: 0,
    scopes_count: 0,
    pricing_points_count: 0,
    valued_targets_count: 0,
  };
  items.forEach((item) => {
    Object.keys(totals).forEach((field) => {
      totals[field] += Number(item?.[field] || 0);
    });
  });
  return {
    ...totals,
    units_count: totals.targets_count + totals.own_units_count,
    children_complete: totals.targets_count === 0 || totals.targets_count === totals.children_known_targets,
    arr_complete: totals.targets_count === 0 || totals.targets_count === totals.valued_targets_count,
  };
}

function facilityTotals(items = []) {
  let arr = 0;
  let mrr = 0;
  let children = 0;
  let known = 0;
  let valued = 0;
  let targets = 0;
  let own = 0;
  let ownChildren = 0;
  let scoreNumerator = 0;
  let scoreDenominator = 0;
  let effectiveNumerator = 0;
  let effectiveDenominator = 0;

  items.forEach((item) => {
    if (item?.is_own_customer) {
      own += 1;
      ownChildren += Number(item.reference_children ?? item.children_count ?? 0);
      return;
    }
    targets += 1;
    arr += Number(item?.potential_arr || 0);
    mrr += Number(item?.potential_mrr || 0);
    children += Number(item?.children_total || 0);
    if (Number(item?.children_known_targets || 0) > 0 || item?.children_complete) known += 1;
    if (item?.arr_complete) valued += 1;
    const weight = Number(item?.potential_arr || 0) > 0 ? Number(item.potential_arr) : 1;
    if (item?.score !== null && item?.score !== undefined) {
      scoreNumerator += Number(item.score) * weight;
      scoreDenominator += weight;
    }
    if (item?.effective_score !== null && item?.effective_score !== undefined) {
      effectiveNumerator += Number(item.effective_score) * weight;
      effectiveDenominator += weight;
    }
  });

  return {
    potential_arr: arr,
    potential_mrr: mrr,
    children_total: children,
    children_known_targets: known,
    children_complete: targets === 0 || targets === known,
    targets_count: targets,
    units_count: items.length,
    own_units_count: own,
    own_children_total: ownChildren,
    valued_targets_count: valued,
    arr_complete: targets === 0 || targets === valued,
    score: scoreDenominator > 0 ? Math.round(scoreNumerator / scoreDenominator) : null,
    effective_score: effectiveDenominator > 0 ? Math.round(effectiveNumerator / effectiveDenominator) : null,
  };
}

function groupFacilities(items = []) {
  const groups = new Map();
  items.forEach((item) => {
    const name = String(item.market_group || 'Pozostałe');
    if (!groups.has(name)) {
      groups.set(name, {
        name, count: 0, targets_count: 0, own_count: 0,
        potential_arr: 0, potential_mrr: 0, children_total: 0,
        children_known_targets: 0, valued_targets_count: 0, items: [],
      });
    }
    const group = groups.get(name);
    group.count += 1;
    if (item.is_own_customer) group.own_count += 1;
    else {
      group.targets_count += 1;
      group.potential_arr += Number(item.potential_arr || 0);
      group.potential_mrr += Number(item.potential_mrr || 0);
      group.children_total += Number(item.children_total || 0);
      if (Number(item.children_known_targets || 0) > 0 || item.children_complete) group.children_known_targets += 1;
      if (item.arr_complete) group.valued_targets_count += 1;
    }
    group.items.push(item.id);
  });

  const order = {
    'Przedszkola Samorządowe': 10,
    'Przedszkola Publiczno-Prywatne': 20,
    'Przedszkola Prywatne': 30,
    'Żłobki publiczne': 40,
    'Żłobki prywatne': 50,
    'MALUCH+ publiczne — etap niepotwierdzony': 55,
    'MALUCH+ prywatne — etap niepotwierdzony': 56,
    'Żłobki – do weryfikacji': 60,
    'Przedszkola – do weryfikacji': 90,
    Pozostałe: 100,
  };
  return [...groups.values()]
    .map((group) => ({
      ...group,
      arr_complete: group.targets_count === 0 || group.targets_count === group.valued_targets_count,
      children_complete: group.targets_count === 0 || group.targets_count === group.children_known_targets,
      pricing_scope: 'facility',
    }))
    .sort((a, b) => (order[a.name] ?? 999) - (order[b.name] ?? 999) || a.name.localeCompare(b.name, 'pl'));
}

export function applyKidplaceClientFilter(data, selectedSegments = [], onlyNewOpenings = false) {
  if (!data || data.product !== 'kidplace') return data;
  const active = new Set(activeDataSegments(selectedSegments));

  if (data?.scope?.level === 'commune') {
    const items = (data.items || []).filter((item) => active.has(String(item.market_segment || 'unverified')) && (!onlyNewOpenings || (item.startup_priority?.priority === true && !item.is_own_customer)));
    return {
      ...data,
      selected_segments: CONTROLLED_SEGMENTS.filter((segment) => selectedSegments.includes(segment)),
      items,
      groups: groupFacilities(items),
      totals: facilityTotals(items),
    };
  }

  const items = (data.items || []).map((item) => ({
    ...item,
    ...combineBuckets(item.segment_buckets || {}, selectedSegments),
    competition_summary: combineCompetitionBuckets(item.competition_buckets || {}, selectedSegments),
    activity_summary: combineActivityBuckets(item.activity_buckets || {}, selectedSegments),
  }));
  return {
    ...data,
    selected_segments: CONTROLLED_SEGMENTS.filter((segment) => selectedSegments.includes(segment)),
    items,
    totals: areaTotals(items),
  };
}
